import { Plus, Trash2, MoonStar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { WEEKDAYS, timeToMinutes, type ShiftForm } from "./time-helpers";

interface Props {
  value: ShiftForm[];
  onChange: (shifts: ShiftForm[]) => void;
  disabled?: boolean;
  /**
   * O que "nenhum turno" significa AQUI.
   *
   * Passou a depender de quem pergunta, e a resposta antiga ("a linha opera 24 horas por
   * dia") ficou falsa em dois dos três consumidores. Hoje o expediente É o tempo
   * planejado do OEE: uma linha com expediente próprio e zero turnos está FECHADA, não aberta o
   * tempo todo. No cenário de simulação, onde "sem calendário" ainda significa contínuo, a frase
   * original continua verdadeira — e é por isso que ela virou prop em vez de sumir.
   */
  emptyHint?: string;
}

const NEW_SHIFT: ShiftForm = { weekday: 1, startTime: "06:00", endTime: "14:00" };

// Editor dos turnos semanais. Controlado (ShiftForm[]). "Cruza a meia-noite" é
// derivado (fim < início); fim == início é sinalizado como inválido. Reusado no cenário.
export function ShiftsEditor({
  value,
  onChange,
  disabled,
  emptyHint = "Sem turnos semanais declarados.",
}: Props) {
  const patch = (i: number, next: Partial<ShiftForm>) =>
    onChange(value.map((s, idx) => (idx === i ? { ...s, ...next } : s)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[13px] font-medium">Turnos semanais</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...value, { ...NEW_SHIFT }])}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Turno
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-subtle px-3 py-4 text-center text-[12px] text-text-tertiary">
          {emptyHint}
        </p>
      ) : (
        <ul className="space-y-2">
          {value.map((shift, i) => {
            const start = timeToMinutes(shift.startTime);
            const end = timeToMinutes(shift.endTime);
            const invalid = start === end;
            const crosses = end < start;
            return (
              <li
                key={i}
                className={cn(
                  "flex flex-wrap items-center gap-2 rounded-md border px-2 py-2",
                  invalid ? "border-rd-danger-500" : "border-border-subtle"
                )}
              >
                <Select
                  aria-label="Dia da semana"
                  className="h-9 w-[92px]"
                  value={shift.weekday}
                  disabled={disabled}
                  onChange={(e) => patch(i, { weekday: Number(e.target.value) })}
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.short}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label="Início"
                  type="time"
                  className="h-9 w-[104px]"
                  value={shift.startTime}
                  disabled={disabled}
                  onChange={(e) => patch(i, { startTime: e.target.value })}
                />
                <span className="text-text-tertiary">–</span>
                <Input
                  aria-label="Fim"
                  type="time"
                  className="h-9 w-[104px]"
                  value={shift.endTime}
                  disabled={disabled}
                  onChange={(e) => patch(i, { endTime: e.target.value })}
                />
                <Input
                  aria-label="Rótulo do turno"
                  placeholder="Rótulo (opcional)"
                  className="h-9 min-w-[120px] flex-1"
                  value={shift.label ?? ""}
                  disabled={disabled}
                  onChange={(e) => patch(i, { label: e.target.value })}
                />
                {crosses ? (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-text-tertiary"
                    title="O turno atravessa a meia-noite"
                  >
                    <MoonStar className="h-3.5 w-3.5" /> vira o dia
                  </span>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover turno"
                  disabled={disabled}
                  onClick={() => remove(i)}
                >
                  <Trash2 className="h-4 w-4 text-rd-danger-500" />
                </Button>
                {invalid ? (
                  <p className="w-full text-[11px] text-rd-danger-500" role="alert">
                    Início e fim não podem ser iguais.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
